import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { createContext, MutableRefObject, PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DRAFT_BULK_UPLOAD_KEY, UpdateDraftBulkUploadRequest, useUpdateBulkUpload } from '@steelbuy/data-access';
import {
    BulkUploadDraftSellerModel,
    createTimestampFromMaybeDate,
    ListingSellerDraftBulkUploadModel,
} from '@steelbuy/ts-shared';
import { toaster } from '@steelbuy/ui-primitive';
import { BulkUploadForm, mapFormValuesToDraft } from '../bulkListingUtil';

type Callback = () => void | null;

type BulkListingContextType = {
    handleSaveDraft: (draftId: string, isSubmission?: boolean) => void;
    updateBulkUploadMutation: UseMutationResult<
        BulkUploadDraftSellerModel,
        unknown,
        UpdateDraftBulkUploadRequest,
        unknown
    >;
    addEditedListing: (listingId: string) => void;
    addDeletedListing: (listingId: string) => void;
    draftSavedSuccessCallback: (callback: Callback) => void;
    uploadedFileRef: MutableRefObject<File | null>;
    resetUpdatedIdsRef: () => void;
    setShowUploadCompleteNotification: (show: boolean) => void;
    showUploadCompleteNotification: boolean;
};

const BulkListingContext = createContext<BulkListingContextType | null>(null);

export const useBulkListingContext = () => {
    const ctx = useContext(BulkListingContext);
    if (!ctx) {
        throw new Error('useBulkListingContext must be used within FormSaveProvider');
    }
    return ctx;
};

export const BulkListingContextProvider = ({ children }: PropsWithChildren) => {
    const { getValues } = useFormContext<BulkUploadForm>();
    const queryClient = useQueryClient();
    const { t } = useTranslation(['translation']);
    const lastSavedFormState = useRef<BulkUploadForm>();
    const updateBulkUploadMutation = useUpdateBulkUpload();
    const editedListingIds = useRef<string[]>([]);
    const deletedListingIds = useRef<string[]>([]);
    const savedSuccessCallback = useRef<Callback>(() => null);
    const uploadedFileRef = useRef<File | null>(null);
    const showUploadCompleteNotification = useRef(false);

    const handleSaveDraft = useCallback((draftId: string, isSubmission?: boolean) => {
        const formData = getValues();
        const savedDraft = queryClient.getQueryData<BulkUploadDraftSellerModel>([DRAFT_BULK_UPLOAD_KEY, draftId]);
        if (savedDraft) {
            const payload: UpdateDraftBulkUploadRequest = {
                bulkId: savedDraft.bulkId,
                bulkDraftReferenceId: savedDraft.bulkDraftReferenceId,
            };
            if (editedListingIds.current.length) {
                const { listings } = formData;
                if (listings?.length) {
                    const updatedListings = listings.filter(({ listingId }) =>
                        editedListingIds.current.includes(listingId)
                    );
                    payload.bulkListingDraftsUpdates = mapFormValuesToDraft(
                        updatedListings,
                        savedDraft.bulkDraftReferenceId
                    ) as ListingSellerDraftBulkUploadModel[];
                }
            }
            if (deletedListingIds.current.length) {
                payload.deletedListingDrafts = deletedListingIds.current;
            }
            if (formData.autoRenew !== undefined) {
                payload.autoRenew = formData.autoRenew;
            }
            if (formData.expireDate) {
                payload.expireDate = createTimestampFromMaybeDate(formData.expireDate).getOrUndefined()?.value;
            }
            updateBulkUploadMutation.mutate(
                { ...payload, isSubmission },
                {
                    onSuccess: () => {
                        editedListingIds.current = [];
                        deletedListingIds.current = [];
                        if (!isSubmission) {
                            toaster.success(t('application.createListing.draftSuccessfulNotification'));
                        }
                        lastSavedFormState.current = formData;
                        savedSuccessCallback.current();
                    },
                    onError: () => {
                        toaster.warn(
                            t(
                                isSubmission
                                    ? 'application.createBulkListing.summary.failedSubmissionMessage'
                                    : 'application.createListing.draftFailedNotification'
                            )
                        );
                    },
                }
            );
        }
    }, []);

    const addEditedListing = useCallback((listingId: string) => {
        if (!editedListingIds.current.includes(listingId)) {
            editedListingIds.current.push(listingId);
        }
    }, []);

    const addDeletedListing = useCallback((listingId: string) => {
        if (!deletedListingIds.current.includes(listingId)) {
            deletedListingIds.current.push(listingId);
        }
    }, []);

    const draftSavedSuccessCallback = useCallback((callback: Callback) => {
        savedSuccessCallback.current = callback;
    }, []);

    const resetUpdatedIdsRef = useCallback(() => {
        editedListingIds.current = [];
        deletedListingIds.current = [];
    }, []);

    const setShowUploadCompleteNotification = useCallback((show: boolean) => {
        showUploadCompleteNotification.current = show;
    }, []);

    const value = useMemo(
        () => ({
            handleSaveDraft,
            updateBulkUploadMutation,
            addEditedListing,
            addDeletedListing,
            draftSavedSuccessCallback,
            uploadedFileRef,
            resetUpdatedIdsRef,
            setShowUploadCompleteNotification,
            showUploadCompleteNotification: showUploadCompleteNotification.current,
        }),
        [
            handleSaveDraft,
            updateBulkUploadMutation,
            addEditedListing,
            addDeletedListing,
            draftSavedSuccessCallback,
            uploadedFileRef,
            resetUpdatedIdsRef,
            setShowUploadCompleteNotification,
            showUploadCompleteNotification,
        ]
    );

    return <BulkListingContext.Provider value={value}>{children}</BulkListingContext.Provider>;
};
